import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Play, CheckCircle2, MapPin, User, Clock, Phone } from "lucide-react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api, formatApiError, asItems, prefixUrl } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";

const STATUS_COLORS = {
  REQUESTED: "bg-amber-100 text-amber-900 border-amber-300",
  SCHEDULED: "bg-blue-100 text-blue-900 border-blue-300",
  IN_PROGRESS: "bg-purple-100 text-purple-900 border-purple-300",
  COMPLETED: "bg-emerald-100 text-emerald-900 border-emerald-300",
  CANCELLED: "bg-rose-100 text-rose-900 border-rose-300",
};

const TABS = [
  { id: "all", label: "All Visits" },
  { id: "pending", label: "Pending" },
  { id: "completed", label: "Completed" },
  { id: "cancelled", label: "Cancelled" },
];

const getImageUrl = (urlObj) => {
  if (!urlObj) return "";
  const url = typeof urlObj === "string" ? urlObj : (urlObj.url || urlObj.file_url || urlObj.public_url || "");
  if (!url) return "";
  return prefixUrl(url);
};

const FALLBACK_IMAGE = "/brand/real-place-mark.svg";

const getVisitImage = (v) => {
  const p = v.property;
  if (!p) return FALLBACK_IMAGE;
  const primary = (Array.isArray(p.images) && p.images[0]) || p.thumbnail || p.thumbnail_url || p.public_url;
  return getImageUrl(primary) || FALLBACK_IMAGE;
};

const formatDateTime = (dtStr) => {
  if (!dtStr) return "—";
  try {
    const d = new Date(dtStr);
    return d.toLocaleDateString("en-IN", {
      month: "short",
      day: "numeric",
      year: "numeric",
    }) + " · " + d.toLocaleTimeString("en-IN", {
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return dtStr;
  }
};

export default function AgentMyVisits() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actioning, setActioning] = useState("");
  const [otpDialog, setOtpDialog] = useState({ open: false, mode: "", sv: null });
  const [otp, setOtp] = useState("");
  const [activeTab, setActiveTab] = useState("all");
  const [error, setError] = useState("");

  const filteredItems = items.filter((v) => {
    const status = String(v.status || "").toUpperCase();
    if (activeTab === "all") return true;
    if (activeTab === "pending") {
      return ["REQUESTED", "SCHEDULED", "IN_PROGRESS"].includes(status);
    }
    if (activeTab === "completed") {
      return status === "COMPLETED";
    }
    if (activeTab === "cancelled") {
      return status === "CANCELLED";
    }
    return true;
  });

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/v1/agent/site-visits/me");
      setItems(asItems(data));
    } catch (e) {
      const message = formatApiError(e);
      setError(message);
      toast.error(message);
    }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openOtp = (sv, mode) => { setOtp(""); setOtpDialog({ open: true, mode, sv }); };
  const closeOtp = () => setOtpDialog({ open: false, mode: "", sv: null });

  // Agents submit only the buyer-provided code. Development OTPs are never read
  // from an API response or displayed in a toast.
  const submitOtp = async () => {
    const sv = otpDialog.sv;
    const ep = otpDialog.mode === "start" ? "start" : "complete";
    setActioning(sv.site_visit_id);
    try {
      const { data } = await api.post(`/v1/site-visits/${sv.site_visit_id}/${ep}`, { otp });
      toast.success(`Visit ${data.status}`);
      closeOtp();
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setActioning(""); }
  };

  return (
    <div className="space-y-6" data-testid="agent-my-visits-page">
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.25em] text-muted-foreground mb-2">Site Visits</p>
        <h1 className="font-display text-4xl font-black tracking-tighter uppercase text-primary">My Assigned Visits</h1>
        <p className="text-muted-foreground mt-2">Start a visit by entering the buyer's start-OTP. Complete it with the completion-OTP.</p>
      </div>

      {/* Dynamic tab selection filter */}
      <div className="w-full sm:w-fit bg-card border border-border/80 p-1 rounded-xl overflow-x-auto scrollbar-none relative flex gap-1">
        {TABS.map(tab => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 sm:flex-none text-center px-3 py-1.5 rounded-lg text-xs font-bold whitespace-nowrap transition-all duration-200 relative z-10 ${
                isActive
                  ? "text-white"
                  : "text-muted-foreground hover:text-foreground"
              }`}
              data-testid={`visit-filter-tab-${tab.id}`}
            >
              {isActive && (
                <motion.div
                  layoutId="activeVisitsTab"
                  className="absolute inset-0 bg-primary rounded-lg z-[-1] shadow-sm"
                  transition={{ type: "spring", stiffness: 380, damping: 30 }}
                />
              )}
              {tab.label}
              {!loading && (
                <span className={`ml-1.5 px-1.5 py-0.2 rounded-full text-[9px] font-black tracking-tight ${
                  isActive ? "bg-white/20 text-white" : "bg-muted text-muted-foreground"
                }`}>
                  {items.filter(v => {
                    const status = String(v.status || "").toUpperCase();
                    if (tab.id === "all") return true;
                    if (tab.id === "pending") return ["REQUESTED", "SCHEDULED", "IN_PROGRESS"].includes(status);
                    if (tab.id === "completed") return status === "COMPLETED";
                    if (tab.id === "cancelled") return status === "CANCELLED";
                    return true;
                  }).length}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="grid gap-3" aria-label="Loading assigned site visits">
          {[0, 1, 2].map((row) => (
            <Card key={row} className="p-5 flex gap-4 rounded-2xl">
              <Skeleton className="h-20 w-20 sm:h-24 sm:w-24 rounded-xl shrink-0" />
              <div className="flex-1 space-y-3"><Skeleton className="h-4 w-1/3" /><Skeleton className="h-5 w-2/3" /><Skeleton className="h-3 w-1/2" /></div>
            </Card>
          ))}
        </div>
      ) : error ? (
        <Card className="border-destructive/30 bg-destructive/5 p-8 text-center" role="alert">
          <p className="font-bold text-foreground">Assigned visits could not be loaded</p>
          <p className="mt-2 text-sm text-muted-foreground">{error}</p>
          <Button type="button" onClick={load} className="mt-4">Try again</Button>
        </Card>
      ) : filteredItems.length === 0 ? (
        <Card className="p-8 text-center text-muted-foreground" data-testid="no-visits">
          {activeTab === "all" ? "No visits assigned yet." : `No ${activeTab} visits found.`}
        </Card>
      ) : (
        <div className="grid gap-3" data-testid="visits-list">
          {filteredItems.map((v) => {
            const propLink = v.property_id ? `/properties/${v.property_id}` : null;
            return (
              <Card key={v.site_visit_id} className="p-3.5 sm:p-5 flex flex-col sm:flex-row gap-3.5 sm:gap-4 bg-card border border-border rounded-2xl hover:shadow-md transition duration-200"
                data-testid={`visit-card-${v.site_visit_id}`}>
                
                <div className="flex gap-3.5 flex-1 min-w-0">
                  {/* Photo Thumbnail */}
                  {propLink ? (
                    <Link to={propLink} className="w-20 h-20 sm:w-24 h-20 sm:h-24 rounded-xl bg-secondary overflow-hidden shrink-0 block group relative shadow-inner">
                      <img src={getVisitImage(v)} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition duration-300"/>
                    </Link>
                  ) : (
                    <div className="w-20 h-20 sm:w-24 h-20 sm:h-24 rounded-xl bg-secondary overflow-hidden shrink-0 block relative shadow-inner">
                      <img src={getVisitImage(v)} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover"/>
                    </div>
                  )}

                  {/* Details Area */}
                  <div className="flex-1 min-w-0 flex flex-col justify-between">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant="outline" className={`text-[9px] sm:text-[10px] font-bold tracking-wider py-0.5 px-2 ${STATUS_COLORS[v.status] || ""}`} data-testid={`visit-status-${v.site_visit_id}`}>
                          {v.status}
                        </Badge>
                        <span className="text-[9px] sm:text-[10px] font-mono bg-muted/60 text-muted-foreground px-1.5 py-0.5 rounded">
                          Ref: {String(v.site_visit_id || v.id || "unavailable").slice(0, 8)}
                        </span>
                      </div>

                      {propLink ? (
                        <Link to={propLink} className="font-display font-black text-base sm:text-lg text-primary leading-snug hover:underline block truncate mt-1">
                          {v.property?.title || "Property Listing"}
                        </Link>
                      ) : (
                        <div className="font-display font-black text-base sm:text-lg text-primary leading-snug mt-1 truncate">
                          {v.property?.title || "Property Listing"}
                        </div>
                      )}

                      {v.property?.city && (
                        <p className="text-[9px] sm:text-[10px] uppercase font-bold tracking-wider text-muted-foreground/80">
                          {v.property.city}
                        </p>
                      )}
                    </div>

                    {/* Visitor details & Time (desktop only) */}
                    <div className="hidden sm:block text-xs text-muted-foreground space-y-1 pt-2 border-t border-dashed border-border/80 mt-2">
                      <div className="flex items-center gap-2">
                        <User className="w-3.5 h-3.5 text-muted-foreground/70 shrink-0"/>
                        <span className="font-semibold text-foreground/90 truncate">{v.visitor_name || "Buyer name unavailable"}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Phone className="w-3.5 h-3.5 text-muted-foreground/70 shrink-0"/>
                        {v.visitor_phone ? <a href={`tel:${v.visitor_phone}`} className="hover:underline hover:text-primary">{v.visitor_phone}</a> : <span>No phone</span>}
                      </div>
                      <div className="flex items-center gap-2 text-foreground font-medium bg-secondary/35 py-1 px-2.5 rounded-md mt-1 w-fit">
                        <Clock className="w-3.5 h-3.5 text-primary/70 shrink-0"/>
                        <span>{formatDateTime(v.scheduled_at)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Mobile Visitor details & Time (rendered full width on mobile below photo-details block) */}
                <div className="block sm:hidden text-xs text-muted-foreground space-y-1.5 pt-2 border-t border-dashed border-border/80 mt-1">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <User className="w-3.5 h-3.5 text-muted-foreground/70 shrink-0"/>
                      <span className="font-semibold text-foreground/95 truncate">{v.visitor_name || "Buyer name unavailable"}</span>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <Phone className="w-3.5 h-3.5 text-muted-foreground/70 shrink-0"/>
                      {v.visitor_phone ? <a href={`tel:${v.visitor_phone}`} className="hover:underline hover:text-primary font-medium">{v.visitor_phone}</a> : <span>No phone</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-foreground font-semibold bg-secondary/40 py-1 px-2.5 rounded-xl w-full">
                    <Clock className="w-3.5 h-3.5 text-primary shrink-0"/>
                    <span>{formatDateTime(v.scheduled_at)}</span>
                  </div>
                </div>

                {/* Actions (Start/Complete/View) */}
                <div className="flex flex-row sm:flex-col sm:justify-center justify-between items-center sm:items-stretch gap-2 shrink-0 self-stretch sm:self-center mt-3 sm:mt-0 pt-2.5 sm:pt-0 border-t sm:border-t-0 border-border/60">
                  {v.status === "SCHEDULED" && (
                    <Button size="sm" className="gap-1.5 font-bold shadow-sm w-full sm:w-auto text-xs py-1.5 px-3" onClick={() => openOtp(v, "start")} disabled={actioning === v.site_visit_id}
                      data-testid={`btn-start-visit-${v.site_visit_id}`}>
                      <Play className="w-3.5 h-3.5 fill-current"/> Start
                    </Button>
                  )}
                  {v.status === "IN_PROGRESS" && (
                    <Button size="sm" className="gap-1.5 font-bold shadow-sm bg-purple-600 hover:bg-purple-700 text-white w-full sm:w-auto text-xs py-1.5 px-3" onClick={() => openOtp(v, "complete")} disabled={actioning === v.site_visit_id}
                      data-testid={`btn-complete-visit-${v.site_visit_id}`}>
                      <CheckCircle2 className="w-3.5 h-3.5"/> Complete
                    </Button>
                  )}
                  {propLink ? (
                    <Link className="inline-flex items-center justify-center text-xs font-bold uppercase tracking-wider py-1.5 px-3 rounded-lg border border-border bg-background hover:bg-muted text-muted-foreground hover:text-primary transition duration-150 text-center w-full sm:w-auto"
                      to={propLink}>
                      <MapPin className="w-3.5 h-3.5 mr-1"/> View
                    </Link>
                  ) : (
                    <a className="inline-flex items-center justify-center text-xs font-bold uppercase tracking-wider py-1.5 px-3 rounded-lg border border-border bg-background hover:bg-muted text-muted-foreground hover:text-primary transition duration-150 text-center w-full sm:w-auto"
                      href={`/properties/${v.property_id}`} target="_blank" rel="noreferrer">
                      <MapPin className="w-3.5 h-3.5 mr-1"/> View
                    </a>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={otpDialog.open} onOpenChange={(v) => !v && closeOtp()}>
        <DialogContent data-testid="otp-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight uppercase">
              {otpDialog.mode === "start" ? "Start Visit — Enter Start OTP" : "Complete Visit — Enter Completion OTP"}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Ask the buyer for the 6-digit code shown in their private ShreeHomes notification.
          </p>
          <Input value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))} maxLength={6}
            placeholder="123456" className="text-center text-lg tracking-[0.5em] font-bold" data-testid="otp-dialog-input" />
          <DialogFooter>
            <Button variant="outline" onClick={closeOtp} data-testid="otp-dialog-cancel">Cancel</Button>
            <Button onClick={submitOtp} disabled={otp.length !== 6 || actioning} className="font-bold gap-2" data-testid="otp-dialog-submit">
              {actioning ? <Loader2 className="w-4 h-4 animate-spin"/> : <CheckCircle2 className="w-4 h-4"/>}
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
